import React, { useState, useCallback, useEffect, useRef } from "react";
import { useSwipeable } from "react-swipeable";
import styled from "styled-components";
import ReviewStars from "./reviews/ReviewStars";
import { Link } from "gatsby";

const TIMER_INTERVAL = 5000;

const ReviewMarqueeContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 420px;
  flex-direction: column;
  padding-top: var(--spacing-3);
  padding-bottom: var(--spacing-5);
  position: relative;

  &:active {
    cursor: grabbing;
  }

  .reviews_container {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    user-select: none;
  }

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    width: 100vw;
    height: 1px;
    border-top: 1px solid var(--indigo-blue-20);
  }

  &::after {
    top: unset;
    bottom: 0;
  }
`;

const ReviewMarqueeDots = styled.div`
  position: absolute;
  bottom: var(--spacing-1_5);
  left: 0;
  right: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  .reviews_dots {
    padding: var(--spacing-0_75);
    display: inline-flex;
    align-items: center;
    border-radius: 100px;
    background-color: var(--cool-20);
    margin: 0 auto;
  }
`;

const ReviewMarqueeDot = styled.div`
  width: 4px;
  height: 4px;
  background-color: var(--cool-40);
  transition: width 0.6s;
  border-radius: 5px;
  margin-right: var(--spacing-0_25);

  &.active {
    width: 60px;
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 0%;
      background-color: var(--yellow-ochre);
      animation: progress ${TIMER_INTERVAL}ms linear forwards;
    }
  }
`;

const ReviewSlide = styled.div`
  opacity: 0;
  animation: fadeIn 0.8s ease-in-out;
  animation-fill-mode: forwards;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  &.fade-out {
    animation: fadeOut 0.8s ease-in-out;
    animation-fill-mode: forwards;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      filter: blur(10px);
    }
    to {
      opacity: 1;
      filter: blur(0px);
    }
  }

  @keyframes fadeOut {
    from {
      opacity: 1;
      filter: blur(0px);
    }
    to {
      opacity: 0;
      filter: blur(10px);
    }
  }

  .review__text {
    margin: var(--spacing-2) 0;

    em {
      font-family: var(--font-dutch);
    }
  }

  .review__author {
    margin-bottom: var(--spacing-0_25);
  }

  .review__product {
    color: var(--indigo-blue-60);
    margin: 0;
  }

  a {
    cursor: pointer;
  }
`;

const StarWrapper = styled.div`
  display: flex;
  transform: translateZ(0);
  backface-visibility: hidden;
  perspective: 1000px;
  -webkit-font-smoothing: antialiased;

  .ReviewStars--star {
    width: 17px;
    height: 16px;
    margin-right: var(--spacing-0_25);
    transform: translateZ(0);
  }
`;

const EmphasisContent = styled.div`
  position: relative;

  > p:first-child {
    z-index: -1;
    color: transparent;
    user-select: none;

    em {
      background-color: var(--yellow-ochre-60);
      filter: blur(10px);
    }
  }

  > p:last-child {
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
`;

export type Review = {
  rating: number;
  reviewContent: {
    childMarkdownRemark: {
      html: string;
    };
  };
  author: string;
  product: {
    name: {
      name: string;
    };
    path: string;
  };
};

const ReviewMarquee = ({ reviews }: { reviews: Review[] }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const timerRef = useRef<any | null>(null);
  const animationRef = useRef<CSSStyleSheet | null>(null);

  const handleSlideChange = useCallback((newIndex: number) => {
    setIsTransitioning(true);
    const currentSlide = document.querySelector(".reviews_container > div");
    if (currentSlide) {
      currentSlide.classList.add("fade-out");
    }

    setTimeout(() => {
      setActiveIndex(newIndex);
      setIsTransitioning(false);
    }, 800);
  }, []);

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    timerRef.current = setInterval(() => {
      if (!isTransitioning) {
        handleSlideChange(
          activeIndex === reviews.length - 1 ? 0 : activeIndex + 1,
        );
      }
    }, TIMER_INTERVAL);
  }, [activeIndex, isTransitioning, handleSlideChange, reviews.length]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsMouseDown(true);
    setStartX(e.clientX);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isMouseDown) return;

    const currentX = e.clientX;
    const diff = startX - currentX;

    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        if (!isTransitioning) {
          handleSlideChange(
            activeIndex === reviews.length - 1 ? 0 : activeIndex + 1,
          );
          resetTimer();
        }
      } else {
        if (!isTransitioning) {
          handleSlideChange(
            activeIndex === 0 ? reviews.length - 1 : activeIndex - 1,
          );
          resetTimer();
        }
      }
      setIsMouseDown(false);
    }
  };

  const handleMouseUp = () => {
    setIsMouseDown(false);
  };

  const { ref: swipeableRef } = useSwipeable({
    onSwipedLeft: () => {
      if (!isTransitioning) {
        handleSlideChange(
          activeIndex === reviews.length - 1 ? 0 : activeIndex + 1,
        );
        resetTimer();
      }
    },
    onSwipedRight: () => {
      if (!isTransitioning) {
        handleSlideChange(
          activeIndex === 0 ? reviews.length - 1 : activeIndex - 1,
        );
        resetTimer();
      }
    },
    trackMouse: true,
    trackTouch: true,
    delta: 10,
    swipeDuration: 500,
  });

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    );

    if (!prefersReducedMotion.matches) {
      resetTimer();
    }

    const style = document.createElement("style");
    style.textContent = `
      @keyframes progress {
        from { width: 0%; }
        to { width: 100%; }
      }
    `;
    document.head.appendChild(style);
    animationRef.current = style.sheet;

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      document.head.removeChild(style);
    };
  }, [resetTimer]);

  if (!reviews || reviews.length === 0) {
    return null;
  }

  const buildReviewSlide = (review: Review, index: number) => {
    return (
      <ReviewSlide key={index}>
        <StarWrapper>
          <ReviewStars score={review.rating} />
        </StarWrapper>
        <EmphasisContent>
          <p
            className="review__text typography-lead2 text-center"
            dangerouslySetInnerHTML={{
              __html: review.reviewContent.childMarkdownRemark.html,
            }}
            aria-hidden="true"
          />
          <p
            className="review__text typography-lead2 text-center"
            dangerouslySetInnerHTML={{
              __html: review.reviewContent.childMarkdownRemark.html,
            }}
          />
        </EmphasisContent>
        <p className="review__author typography-label2 text-center">
          {review.author}
        </p>
        <Link
          to={review.product.path}
          title={`Go to ${review.product.name.name} product page`}
          className="review__product typography-body2 font-circular text-center"
        >
          {review.product.name.name}
        </Link>
      </ReviewSlide>
    );
  };

  const activeChild = reviews[activeIndex];

  return (
    <ReviewMarqueeContainer
      className="swipeable"
      ref={swipeableRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div className="reviews_container" aria-live="polite" aria-atomic="true">
        {activeChild && buildReviewSlide(activeChild, activeIndex)}
      </div>
      <ReviewMarqueeDots>
        <div className="reviews_dots">
          {reviews.map((_, index) => (
            <ReviewMarqueeDot
              key={"review-marquee-dot" + index}
              className={index === activeIndex ? "active" : ""}
              aria-label={`Slide ${index + 1} of ${reviews.length} ${index === activeIndex ? "active" : ""}`}
            />
          ))}
        </div>
      </ReviewMarqueeDots>
    </ReviewMarqueeContainer>
  );
};

export default ReviewMarquee;
